import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Navigate, useLocation } from "react-router";
import { api, clearTokens } from "@/lib/api";

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  agencyId: string;
  permissionLevel: string;
  mfaEnabled: boolean;
  createdAt?: string;
}

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
  mfaRequired?: boolean;
  mfaChallengeToken?: string;
}

interface MfaVerifyResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<LoginResponse>;
  verifyMfa: (code: string, mfaToken: string) => Promise<void>;
  logout: () => void;
  hasRole: (...roles: string[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = user !== null;

  // Attempt to restore session on mount
  useEffect(() => {
    const tokens = api.getTokens();
    if (!tokens?.accessToken) {
      setIsLoading(false);
      return;
    }

    api
      .get<{ user: User }>("/auth/me")
      .then((res) => setUser(res.user))
      .catch(() => {
        clearTokens();
      })
      .finally(() => setIsLoading(false));
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResponse> => {
      const res = await api.post<LoginResponse>("/auth/login", {
        email,
        password,
      });

      if (!res.mfaRequired) {
        api.setTokens({
          accessToken: res.accessToken,
          refreshToken: res.refreshToken,
        });
        setUser(res.user);
      }

      return res;
    },
    [],
  );

  const verifyMfa = useCallback(
    async (code: string, mfaToken: string): Promise<void> => {
      const res = await api.post<MfaVerifyResponse>("/auth/mfa/verify", {
        totpCode: code,
        mfaChallengeToken: mfaToken,
      });
      api.setTokens({
        accessToken: res.accessToken,
        refreshToken: res.refreshToken,
      });
      setUser(res.user);
    },
    [],
  );

  const logout = useCallback(() => {
    api.post("/auth/logout").catch(() => {
      // Ignore errors on logout
    });
    clearTokens();
    setUser(null);
  }, []);

  const hasRole = useCallback(
    (...roles: string[]) => {
      if (!user) return false;
      return roles.includes(user.permissionLevel);
    },
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      login,
      verifyMfa,
      logout,
      hasRole,
    }),
    [user, isAuthenticated, isLoading, login, verifyMfa, logout, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}

export function AuthGuard({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
